// components/ImageUploader.js
import React, { useState, useEffect, useContext } from 'react';
import { Button, FormGroup, Label, Alert, Spinner } from 'reactstrap';
import { useSelector } from 'react-redux';
import ImageRecordsService from '../../helpers/imageRecords_helper';

const ImageUploader = ({ onImagesChange, maxImages = 10, existingImages = [], onCameraOpen, mostrarLabel = false }) => {
    const tenantId = useSelector((state) => state.Context.tenantId);
    const token = useSelector((state) => state.Token.token);
    const [imagenes, setImagenes] = useState(existingImages);
    useEffect(() => {
        console.log(`[ImageUploader] Component mounted with auth data`, {
            hasTenantId: !!tenantId,
            tenantId: tenantId,
            hasToken: !!token,
            tokenLength: token ? token.length : 0,
            existingImagesCount: existingImages.length
        });
        console.log(`[ImageUploader] Existing images: `, { existingImages });
    }, [tenantId, token, imagenes, existingImages]);


    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});

    // Cargar PWA Elements para la cámara en web
    useEffect(() => {
        const loadPWAElements = async () => {
            if (typeof window !== 'undefined') {
                try {
                    const { defineCustomElements } = await import('@ionic/pwa-elements/loader');
                    defineCustomElements(window);
                } catch (error) {
                    console.log('PWA Elements no disponibles en este entorno');
                }
            }
        };

        loadPWAElements();
    }, []);

    /**
 * Convierte una URI local (webPath o path de Capacitor) a un objeto Blob/File.
 * @param imageUri La URI de la imagen obtenida de Camera.getPhoto().
 * @returns {Promise<Blob>} Los datos binarios de la imagen.
 */
    const convertToFile = async (imageUri) => {
        try {
            // Usa fetch() para obtener los datos de la URI temporal
            const response = await fetch(imageUri);
            // Convierte la respuesta a un objeto binario Blob
            const blob = await response.blob();
            return blob;
        } catch (error) {
            console.error("Error al convertir URI a Blob:", error);
            throw new Error("No se pudo obtener el archivo de la URI.");
        }
    }

    const tomarFoto = async () => {
        try {
            if (imagenes.length >= maxImages) {
                setError(`Máximo ${maxImages} imágenes permitidas`);
                return;
            }

            const { Camera } = await import('@capacitor/camera');

            // Notificar al componente padre que la cámara se va a abrir
            if (onCameraOpen) {
                onCameraOpen(true);
            }


            const image = await Camera.getPhoto({
                quality: 85,
                allowEditing: false,
                resultType: 'uri',
                source: 'CAMERA',
                promptLabelHeader: 'Tomar Foto',
                promptLabelPhoto: 'Desde Galería',
                promptLabelPicture: 'Usar Cámara'
            });
            const imageFile = await convertToFile(image.webPath || image.path);
            // Notificar que la cámara se cerró
            if (onCameraOpen) {
                onCameraOpen(false);
            }


            console.log(`[ImageUploader] Photo captured`, {
                imageFile,
                imageDetails: {
                    format: image.format,
                    webPath: image.webPath,
                }
            });
            if (imageFile) {
                const nuevaImagen = {
                    id: Date.now() + Math.random(),
                    formato: image.format,
                    fecha: new Date().toISOString(),
                    nombre: `foto_${new Date().getTime()}.${image.format}`,
                    file: imageFile,
                    path: image.webPath || image.path
                };

                console.log(`[ImageUploader] Image uploaded`, { nuevaImagen });

                setImagenes(prev => {
                    const nuevas = [...prev, nuevaImagen];
                    onImagesChange(nuevas);
                    return nuevas;
                });
                setError('');
            }
        } catch (error) {
            // Asegurarse de notificar el cierre incluso en error
            if (onCameraOpen) {
                onCameraOpen(false);
            }
            if (error.message !== 'User cancelled photos app') {
                console.error('Error al tomar foto:', error);
                setError('Error al acceder a la cámara');
            }
        }
    };

    const seleccionarDeGaleria = async () => {
        try {
            if (imagenes.length >= maxImages) {
                setError(`Máximo ${maxImages} imágenes permitidas`);
                return;
            }

            const { Camera } = await import('@capacitor/camera');

            const image = await Camera.getPhoto({
                quality: 85,
                allowEditing: false,
                resultType: 'uri',
                source: 'PHOTOS',
                promptLabelHeader: 'Seleccionar Imagen',
                promptLabelPhoto: 'Desde Galería',
                promptLabelPicture: 'Usar Cámara'
            });

            const imageFile = await convertToFile(image.webPath || image.path);


            if (image) {
                const nuevaImagen = {
                    id: Date.now() + Math.random(),
                    base64: image.base64String,
                    formato: image.format,
                    fecha: new Date().toISOString(),
                    nombre: `imagen_${new Date().getTime()}.${image.format}`,
                    file: imageFile,
                    path: image.webPath || image.path
                };

                setImagenes(prev => {
                    const nuevas = [...prev, nuevaImagen];
                    onImagesChange(nuevas);
                    return nuevas;
                });
                setError('');
            }
        } catch (error) {
            if (error.message !== 'User cancelled photos app') {
                console.error('Error al seleccionar imagen:', error);
                setError('Error al acceder a la galería');
            }
        }
    };

    const eliminarImagen = (id) => {
        const nuevasImagenes = imagenes.filter(img => img.id !== id);
        setImagenes(nuevasImagenes);
        onImagesChange(nuevasImagenes);
        setError('');
    };

    const subirImagenServidor = async (imagen) => {
        const uploadStartTime = Date.now();

        try {
            setUploading(true);
            // Actualizar estado de progreso para esta imagen
            setUploadProgress(prev => ({
                ...prev,
                [imagen.id]: { uploading: true, progress: 0 }
            }));

            console.log(`[ImageUploader] Calling ImageRecordsService.uploadImage`);
            const result = await ImageRecordsService.uploadImage(imagen, { tenantId });

            const uploadDuration = Date.now() - uploadStartTime;
            console.log(`[ImageUploader] Upload completed in ${uploadDuration}ms`, {
                result,
                hasSuccess: !!result.success,
                hasData: !!result.data
            });

            if (result.id) {
                // Actualizar imagen con la URL del servidor
                const imagenActualizada = {
                    ...imagen,
                    recordUuid: result.id,
                    subido: true
                };

                console.log(`[ImageUploader] Image updated successfully`, imagenActualizada);

                // Actualizar estado de progreso
                setUploadProgress(prev => ({
                    ...prev,
                    [imagen.id]: { uploading: false, progress: 100, success: true }
                }));

                return imagenActualizada;
            } else {
                console.error(`[ImageUploader] Upload failed - no success flag`, {
                    result,
                    message: result.message
                });
                throw new Error(result.message || 'Error al subir imagen');
            }
        } catch (error) {
            const uploadDuration = Date.now() - uploadStartTime;

            console.error(`[ImageUploader] Upload failed after ${uploadDuration}ms`, {
                error: error.message,
                errorType: error.constructor.name,
                errorStack: error.stack,
                imageId: imagen.id,
                imageName: imagen.nombre,
                isTimeout: error.code === 'ECONNABORTED',
                hasTenantId: !!tenantId,
                hasToken: !!token
            });

            // Actualizar estado de progreso con error
            setUploadProgress(prev => ({
                ...prev,
                [imagen.id]: { uploading: false, progress: 0, error: error.message }
            }));

            throw error;
        } finally {
            setUploading(false);
        }
    };

    const subirTodasImagenes = async () => {
        const batchUploadStartTime = Date.now();

        try {
            console.log(`[ImageUploader] Starting batch upload`, {
                totalImages: imagenes.length,
                imagesToUpload: imagenes.filter(img => !img.subido).length,
                alreadyUploaded: imagenes.filter(img => img.subido).length
            });

            setError('');
            setUploading(true);
            const resultados = [];

            for (const imagen of imagenes) {
                if (!imagen.subido) { // Solo subir si no está en servidor
                    console.log(`[ImageUploader] Processing image ${imagen.id}: ${imagen.nombre}`);
                    try {
                        const imagenActualizada = await subirImagenServidor(imagen);
                        resultados.push(imagenActualizada);
                        console.log(`[ImageUploader] Successfully uploaded image ${imagen.id}`);
                    } catch (error) {
                        // Continuar con otras imágenes incluso si una falla
                        console.error(`[ImageUploader] Error uploading image ${imagen.id}`, {
                            error: error.message,
                            imageName: imagen.nombre,
                            willContinue: true
                        });
                        resultados.push({
                            ...imagen,
                            error: error.message
                        });
                    }
                } else {
                    console.log(`[ImageUploader] Skipping already uploaded image ${imagen.id}`);
                    resultados.push(imagen);
                }
            }

            const batchUploadDuration = Date.now() - batchUploadStartTime;
            console.log(`[ImageUploader] Batch upload completed in ${batchUploadDuration}ms`, {
                totalProcessed: resultados.length,
                successful: resultados.filter(img => img.subido && !img.error).length,
                failed: resultados.filter(img => img.error).length
            });

            setImagenes(resultados);
            onImagesChange(resultados);

            // Verificar si hubo errores
            const errores = resultados.filter(img => img.error);
            if (errores.length > 0) {
                const errorMsg = `${errores.length} imágenes no pudieron subirse. Revise el estado de cada imagen.`;
                console.error(`[ImageUploader] Batch upload completed with errors`, {
                    errorCount: errores.length,
                    errorMessage: errorMsg
                });
                setError(errorMsg);
            } else {
                console.log(`[ImageUploader] Batch upload completed successfully - all images uploaded`);
                setError('');
            }

            return resultados;
        } catch (error) {
            const batchUploadDuration = Date.now() - batchUploadStartTime;
            console.error(`[ImageUploader] Batch upload failed after ${batchUploadDuration}ms`, {
                error: error.message,
                errorType: error.constructor.name,
                totalImages: imagenes.length
            });
            setError('Error al subir imágenes al servidor');
            throw error;
        } finally {
            setUploading(false);
        }
    };

    return (
        <FormGroup>
            {mostrarLabel && <Label>Imágenes</Label>}

            {error && (
                <Alert color="danger" className="py-2" timeout={0}>
                    <small>{error}</small>
                </Alert>
            )}

            {/* Botones de acción */}
            <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                    type="button"
                    color="primary"
                    outline
                    onClick={tomarFoto}
                    disabled={imagenes.length >= maxImages}
                    className="d-flex align-items-center gap-1"
                >
                    <i className="ri-camera-line me-1"></i>
                    Cámara
                </Button>

                <Button
                    type="button"
                    color="secondary"
                    outline
                    onClick={seleccionarDeGaleria}
                    disabled={imagenes.length >= maxImages}
                    className="d-flex align-items-center gap-1"
                >
                    <i className="ri-image-line me-1"></i>
                    Galería
                </Button>

                {imagenes.length > 0 && (
                    <Button
                        type="button"
                        color="success"
                        onClick={subirTodasImagenes}
                        disabled={uploading || imagenes.every(img => img.subido)}
                        className="d-flex align-items-center gap-1"
                    >
                        {uploading ? (
                            <>
                                <Spinner size="sm" />
                                Subiendo...
                            </>
                        ) : (
                            <>
                                <i className="ri-upload-cloud-line me-1"></i>
                                Subir al Servidor
                            </>
                        )}
                    </Button>
                )}
            </div>

            {/* Contador de imágenes */}
            {maxImages && (
                <div className="mb-2">
                    <small className="text-muted">
                        {imagenes.length} / {maxImages} imágenes
                    </small>
                </div>
            )}

            {/* Vista previa de imágenes */}
            {imagenes.length > 0 && (
                <div className="row">
                    {imagenes.map((imagen) => (
                        <div key={imagen.id} className="col-6 col-md-4 col-lg-3 mb-3">
                            <div className="card h-100">
                                <div className="position-relative">
                                    <img
                                        src={imagen.path}
                                        alt="Vista previa"
                                        className="card-img-top"
                                        style={{
                                            height: '120px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                    <div className="position-absolute top-0 end-0 p-1">
                                        {imagen.subido ? (
                                            <span className="badge bg-success">
                                                <i className="ri-check-line"></i>
                                            </span>
                                        ) : uploadProgress[imagen.id]?.uploading ? (
                                            <span className="badge bg-primary">
                                                <Spinner size="sm" />
                                            </span>
                                        ) : uploadProgress[imagen.id]?.error ? (
                                            <span className="badge bg-danger" title={uploadProgress[imagen.id].error}>
                                                <i className="ri-error-warning-line"></i>
                                            </span>
                                        ) : null}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm position-absolute top-0 start-0 m-1"
                                        onClick={() => eliminarImagen(imagen.id)}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            padding: 0,
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="card-body p-2">
                                    <small className="text-muted d-block">
                                        {new Date(imagen.fecha).toLocaleTimeString()}
                                    </small>
                                    {imagen.subido && (
                                        <small className="text-success d-block">
                                            <i className="ri-check-line"></i> Subida
                                        </small>
                                    )}
                                    {uploadProgress[imagen.id]?.error && (
                                        <small className="text-danger d-block">
                                            <i className="ri-error-warning-line"></i> Error
                                        </small>
                                    )}
                                    {imagen.recordUuid && (
                                        <small className="text-info d-block">
                                            ID: {imagen.recordUuid.substring(0, 8)}...
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {imagenes.length === 0 && (
                <div className="text-center py-4 border rounded bg-light">
                    <i className="ri-image-line display-4 text-muted"></i>
                    <p className="text-muted mt-2 mb-0">No hay imágenes adjuntas</p>
                </div>
            )}
        </FormGroup>
    );
};

export default ImageUploader;